import { CommonModule } from '@angular/common';
import { Component, effect, forwardRef, signal, untracked } from '@angular/core';
import { AbstractControl, type ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, type ValidationErrors, type Validator } from '@angular/forms';
import { form, FormField } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { type ScheduleConfigDto } from '@api/devices';

interface ScheduleInternalValue {
  enabled: boolean;
  endTime: string;
  startTime: string;
  weekdays: number[];
}

@Component({
  imports: [
    CommonModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormField,
  ],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScheduleControlComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ScheduleControlComponent),
    },
  ],
  selector: 'avs-schedule-control',
  styleUrl: './schedule-control.component.scss',
  templateUrl: './schedule-control.component.html',
})
export class ScheduleControlComponent implements ControlValueAccessor, Validator {
  model = signal<ScheduleInternalValue>({
    enabled: false,
    endTime: '23:59',
    startTime: '00:00',
    weekdays: [],
  });
  scheduleForm = form(this.model);

  weekdays = [
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
    { label: 'Sun', value: 7 },
  ];

  constructor() {
    effect(() => {
      const output = this.mapToOutput();

      untracked(() => {
        this.onChange(output);
        this.onTouched();
      });
    });
  }

  onChange = (_val: { config: ScheduleConfigDto | null; enabled: boolean } | null) => {
    // replaced in registerOnChange
  };
  onTouched = () => {
    // replaced in registerOnTouched
  };

  writeValue(val: { config: ScheduleConfigDto | null; enabled: boolean } | null) {
    if (val) {
      const internalValue = this.mapToInternal(val);

      this.model.set(internalValue);
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    const { enabled, startTime, endTime } = this.model();

    if (!enabled) {
      return null;
    }

    const errors: ValidationErrors = {};

    if (!startTime) {
      errors['startTimeRequired'] = true;
    }
    if (!endTime) {
      errors['endTimeRequired'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  setDisabledState(_isDisabled: boolean) {
    // not supported
  }

  toggleWeekday(day: number) {
    const current = this.model().weekdays;

    if (current.includes(day)) {
      this.model.update(m => ({ ...m, weekdays: m.weekdays.filter(d => d !== day) }));
    } else {
      this.model.update(m => ({ ...m, weekdays: [...m.weekdays, day] }));
    }
  }

  isWeekdaySelected(day: number): boolean {
    return this.model().weekdays.includes(day);
  }

  private mapToOutput(): { config: ScheduleConfigDto | null; enabled: boolean } {
    const { enabled, startTime, endTime, weekdays } = this.model();

    if (!enabled) {
      return { config: null, enabled: false };
    }

    const workingHours: Record<string, { end: string; start: string }> = {};

    weekdays.forEach((day: number) => {
      workingHours[day.toString()] = { end: endTime, start: startTime };
    });

    return {
      config: {
        workingHours,
      },
      enabled: true,
    };
  }

  private mapToInternal(val: { config: ScheduleConfigDto | null; enabled: boolean }): ScheduleInternalValue {
    const enabled = val.enabled;
    let startTime = '00:00';
    let endTime = '23:59';
    const weekdays: number[] = [];

    if (val.config?.workingHours && Object.keys(val.config.workingHours).length > 0) {
      const keys = Object.keys(val.config.workingHours);
      const firstKey = keys[0];

      startTime = val.config.workingHours[firstKey].start ?? startTime;
      endTime = val.config.workingHours[firstKey].end ?? endTime;
      keys.forEach(key => weekdays.push(parseInt(key, 10)));
    }

    return {
      enabled,
      endTime,
      startTime,
      weekdays,
    };
  }
}
